import type { MapRef } from 'react-map-gl/mapbox'

import { Alert } from '@/components/ui'
import { useDashboard } from '@/hooks'

import AssetTable from './AssetTable'
import { useMapContext } from './MapContext'
import { useStatusColumnsContext } from './StatusColumns'
import { AssetStateSummary } from './types'

interface AssetPanelProps {
  mapRef: React.RefObject<MapRef | null>
  setCurrentAssetId: (id: number | undefined, origin: string) => void
  onFourthColRightMeasured?: (edge: number) => void
  onHeaderMeasured?: (rect: { top: number; height: number; right: number }) => void
}

export const AssetPanel = ({
  mapRef,
  setCurrentAssetId,
  onFourthColRightMeasured,
  onHeaderMeasured,
}: AssetPanelProps) => {
  const context = useMapContext()
  const { statusColumns, isLoadingColumns } = useStatusColumnsContext()
  const { configuration } = useDashboard()

  const assets = context.sidebarState.limitToMap ? context.viewportFilteredAssets : context.filteredAssets

  const showSkeleton =
    context.isSwitchingView ||
    isLoadingColumns ||
    (context.isLoadingAssets && context.allAssets.length === 0 && context.filters.companyId)

  const handleAssetClick = (asset: AssetStateSummary) => {
    setCurrentAssetId(asset.id, 'assetPanel')
    const map = mapRef.current?.getMap()
    if (map) {
      map.flyTo({
        center: [asset.location.longitude, asset.location.latitude],
        duration: 1000,
      })
    }
    if (context.sidebarState.layoutMode === 'table') {
      context.setAssetDetailSheetIsOpen(true)
    }
  }

  if (showSkeleton) {
    return (
      <div className="flex flex-col min-h-0 w-full h-full overflow-hidden">
        <div className="bg-primary h-10 shrink-0" />
        <div className="flex-1 overflow-hidden flex flex-col">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className={`h-[35px] shrink-0 flex items-center gap-3 px-4 border-b
  border-base-200 ${i % 2 === 0 ? 'bg-base-100' : 'bg-base-200'}`}
            >
              <div className="skeleton h-4 w-16" />
              <div className="skeleton h-4 w-6" />
              <div className="skeleton h-4 w-6" />
              <div className="skeleton h-4 flex-1 max-w-48" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!context.filters.companyId && context.allAssets.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <span className="text-sm text-base-content/70 text-center">
          Choose a company{Object.keys(configuration.assetMaps || {}).length > 0 ? ' or saved map' : ''} to load assets.
        </span>
      </div>
    )
  }

  if (context.allAssets.length === 0) return null

  return (
    <div className="flex flex-col min-h-0 w-full h-full overflow-hidden">
      {context.error && !context.isLoadingAssets && <Alert message={context.error} type="error" />}

      <AssetTable
        assets={assets}
        onAssetClick={handleAssetClick}
        currentAssetId={context.currentAssetId}
        columns={statusColumns}
        companyId={context.filters.companyId}
        onFourthColRightMeasured={onFourthColRightMeasured}
        onHeaderMeasured={onHeaderMeasured}
      />
    </div>
  )
}
